// What the emailed magic link opens (openspec: image-audit-tool).
//
// This page sits OUTSIDE ProtectedRoute — redeeming the link is what creates
// the session, so requiring one first would be circular. It is the only route
// in the portal with that property.
//
// The account it opens is `pending`. That is deliberate and must stay visible:
// an audit proves nothing about who owns the audited site, so the page invites
// verification rather than pretending the account is ready.

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';

type Report = Awaited<ReturnType<typeof apiService.getAuditReport>>;

const AuditReportPage: React.FC = () => {
  const [params] = useSearchParams();
  const { loginWithAuditToken } = useAuth();
  const [state, setState] = useState<'opening' | 'ready' | 'expired' | 'error'>('opening');
  const [report, setReport] = useState<Report | null>(null);
  // A magic link is single-use, and React 18 StrictMode mounts effects twice in
  // development. Without this guard the second run spends the token and the
  // first render's session is orphaned.
  const redeemed = useRef(false);

  useEffect(() => {
    const linkToken = params.get('token');
    if (!linkToken) { setState('expired'); return; }
    if (redeemed.current) return;
    redeemed.current = true;

    (async () => {
      try {
        const reportId = await loginWithAuditToken(linkToken);
        if (reportId) setReport(await apiService.getAuditReport(reportId));
        setState('ready');
      } catch {
        // Used or expired is by far the likeliest failure — email clients
        // prefetch links — so say that rather than "something went wrong".
        setState('expired');
      }
    })();
  }, [params, loginWithAuditToken]);

  if (state === 'opening') {
    return <div className="audit-report"><p>Opening your report…</p></div>;
  }

  if (state === 'expired') {
    return (
      <div className="audit-report">
        <h1>This report link is no longer available</h1>
        <p>
          Report links work once and last three days. Run the check again and we'll
          send a fresh one.
        </p>
        <p><a href="https://imgaff.com/audit.html">Audit a post again</a></p>
      </div>
    );
  }

  if (state === 'error' || !report) {
    return (
      <div className="audit-report">
        <h1>We couldn't load that report</h1>
        <p>You're signed in — try the <a href="/">dashboard</a>, or email hello@imgaff.com.</p>
      </div>
    );
  }

  const grouped = (report.subjects || []).reduce((n: number, s: any) => n + s.images, 0);

  return (
    <div className="audit-report">
      <p className="eyebrow">Your image-opportunity report</p>
      <h1>{report.title || 'Audited page'}</h1>
      <p className="audit-url">{report.url}</p>

      <section className="audit-section">
        <h2>Matching readiness</h2>
        <ul className="audit-stats">
          <li><b>{report.images}</b> images in the content</li>
          <li><b>{report.missingDescription}</b> without a description</li>
          <li><b>{report.distinctDescriptions}</b> distinct descriptions</li>
          {!!report.linkedToAttachmentPage && (
            <li><b>{report.linkedToAttachmentPage}</b> linking to an attachment page</li>
          )}
        </ul>
      </section>

      {!!(report.subjects && report.subjects.length) && (
        <section className="audit-section">
          <h2>Product subjects these images could support</h2>
          <ul className="audit-subjects">
            {report.subjects.map((s: any) => (
              <li key={s.subject}><b>{s.images}</b> — {s.subject}</li>
            ))}
          </ul>
          {/* Never let a short list read as the whole page */}
          {!!report.remainder && (
            <p className="audit-note">
              {report.remainder} of the {grouped + report.remainder} described images
              didn't group cleanly into any of these.
            </p>
          )}
        </section>
      )}

      <section className="audit-section audit-next">
        <h2>Turn the opportunity into eligible matches</h2>
        <p>
          This report is saved to your account. Auditing a public URL did not prove
          ownership, so verify the site before connecting the affiliate product feeds
          you choose. After that, install ImgAff and watch for real eligible matches.
        </p>
        <p><a href="/#website-verification">Verify your site</a></p>
      </section>
    </div>
  );
};

export default AuditReportPage;
